import React, { useRef, useState } from 'react'
import { HiOutlineFilm, HiOutlineRefresh, HiOutlineTrash, HiOutlineDeviceMobile } from 'react-icons/hi'
import ItemService from '../service/ItemService'
import Modal from './Modal'
import VideoUploader from './VideoUploader'
import { formatSeconds, MAX_VIDEO_SECONDS } from '../util/videoFile'

// Staff item editor: the product's optional portrait video. Picking a file opens VideoUploader (checked and
// compressed in the browser), then it uploads with a progress bar. Switches: "Show video" (hide without deleting)
// and "Video first" (before the photos instead of after the main photo). Shoppers see it on phones only.
const ItemVideoCard = ({ itemId, video, onChange, onToast }) => {
    const fileRef = useRef(null)
    const [source, setSource] = useState(null) // the picked File while VideoUploader is open
    const [uploadProgress, setUploadProgress] = useState(null) // 0..1 while uploading
    const [saving, setSaving] = useState('') // 'enabled' | 'first' while a switch saves
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState('')

    const hasVideo = !!(video && video.videoUrl)
    const uploading = uploadProgress != null

    const pick = () => {
        setError('')
        if (fileRef.current) fileRef.current.click()
    }

    const onFile = (e) => {
        const file = e.target.files && e.target.files[0]
        e.target.value = ''
        if (file) setSource(file)
    }

    const onPrepared = ({ file, poster }) => {
        setSource(null)
        const formData = new FormData()
        formData.append('itemId', itemId)
        formData.append('file', file)
        formData.append('poster', poster)
        setUploadProgress(0)
        ItemService.uploadItemVideo(formData, setUploadProgress)
            .then(res => {
                onChange(res.data.data)
                onToast(hasVideo ? 'Video replaced' : 'Video added')
            })
            .catch(err => setError(err?.response?.data?.msg || 'Could not upload the video. Please try again.'))
            .finally(() => setUploadProgress(null))
    }

    const onPrepareCancel = (message) => {
        setSource(null)
        if (message) setError(message)
    }

    const toggle = (key) => {
        if (saving || !hasVideo) return
        const next = !video[key]
        const previous = video
        setSaving(key)
        setError('')
        onChange({ ...video, [key]: next })
        ItemService.updateItemVideo(itemId, { [key]: next })
            .then(res => {
                onChange(res.data.data)
                if (key === 'enabled') onToast(next ? 'Video shown in the shop' : 'Video hidden from the shop')
                else onToast(next ? 'Video shows first' : 'Video shows after the main photo')
            })
            .catch(() => {
                onChange(previous)
                setError('Could not save. Please try again.')
            })
            .finally(() => setSaving(''))
    }

    const remove = () => {
        setDeleting(true)
        ItemService.deleteItemVideo(itemId)
            .then(res => {
                onChange(res.data.data)
                setConfirmDelete(false)
                onToast('Video deleted')
            })
            .catch(() => setError('Could not delete the video. Please try again.'))
            .finally(() => setDeleting(false))
    }

    const percent = Math.round((uploadProgress || 0) * 100)

    return (
        <section className='mv-card mv-video-card' aria-labelledby='mv-video-card-title'>
            <div className='mv-video-card-head'>
                <h2 id='mv-video-card-title' className='mv-card-title'>Video</h2>
                <span className='mv-chip mv-chip-muted mv-video-card-chip'><HiOutlineDeviceMobile aria-hidden='true' /> Phones only</span>
            </div>

            {hasVideo ?
                <div className='mv-video-card-body'>
                    <video className='mv-video-card-player' src={video.videoUrl} poster={video.posterUrl}
                        controls playsInline muted preload='metadata' />
                    <div className='mv-video-card-side'>
                        <p className='mv-video-card-meta'>
                            {video.durationMs ? formatSeconds(video.durationMs / 1000) : ''} · portrait
                        </p>

                        <label className={'mv-switch' + (saving === 'enabled' ? ' is-busy' : '')}>
                            <input type='checkbox' checked={!!video.enabled} onChange={() => toggle('enabled')} disabled={!!saving || uploading} />
                            <span className='mv-switch-track' aria-hidden='true'><span className='mv-switch-thumb' /></span>
                            <span className='mv-switch-label'>Show video</span>
                        </label>
                        <label className={'mv-switch' + (saving === 'first' ? ' is-busy' : '')}>
                            <input type='checkbox' checked={!!video.first} onChange={() => toggle('first')}
                                disabled={!!saving || uploading || !video.enabled} />
                            <span className='mv-switch-track' aria-hidden='true'><span className='mv-switch-thumb' /></span>
                            <span className='mv-switch-label'>Video first</span>
                        </label>
                        <p className='mv-field-hint'>
                            {!video.enabled ? 'Hidden: shoppers only see the photos.'
                                : video.first ? 'Shoppers see the video before the photos.'
                                    : 'Shoppers see the main photo, then the video.'}
                        </p>

                        <div className='mv-video-card-actions'>
                            <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm' onClick={pick} disabled={uploading || !!saving}>
                                <HiOutlineRefresh aria-hidden='true' /> Replace
                            </button>
                            <button type='button' className='mv-btn mv-btn-danger mv-btn-sm' onClick={() => setConfirmDelete(true)} disabled={uploading || !!saving}>
                                <HiOutlineTrash aria-hidden='true' /> Delete
                            </button>
                        </div>
                    </div>
                </div> :
                !uploading &&
                <button type='button' className='mv-video-card-empty' onClick={pick}>
                    <HiOutlineFilm aria-hidden='true' />
                    <span className='mv-video-card-empty-title'>Add a video</span>
                    <span className='mv-video-card-empty-text'>Portrait, up to {MAX_VIDEO_SECONDS} seconds. We make it light for phones.</span>
                </button>}

            {uploading &&
                <div className='mv-video-card-upload' role='status'>
                    <span>Uploading video… {percent}%</span>
                    <div className='mv-video-progress mv-video-progress-light' role='progressbar' aria-label='Uploading video'
                        aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
                        <span style={{ width: Math.max(4, percent) + '%' }} />
                    </div>
                </div>}

            {error && <div className='mv-alert mv-alert-error' role='alert'>{error}</div>}

            <input type='file' ref={fileRef} accept='video/*' onChange={onFile} hidden />

            {source && <VideoUploader file={source} onDone={onPrepared} onCancel={onPrepareCancel} />}

            {confirmDelete &&
                <Modal title='Delete this video?' onClose={() => setConfirmDelete(false)} busy={deleting}
                    actions={<>
                        <button type='button' className='mv-btn mv-btn-secondary' onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
                        <button type='button' className='mv-btn mv-btn-danger-solid' onClick={remove} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</button>
                    </>}>
                    <p className='mv-modal-text'>The video will be removed from the product. Photos are not affected. This cannot be undone.</p>
                </Modal>}
        </section>
    )
}

export default ItemVideoCard
